'use client';

import Link from 'next/link';
import { PayloadImage } from '@/components/PayloadImage';

type Rating = {
  platform?: 'google' | 'capterra' | 'g2' | 'other';
  label?: string;
  score?: string;
  stars?: number;
};

type HeroBBlock = {
  eyebrowBadge?: string;
  eyebrowBold?: string;
  eyebrowText?: string;
  heading?: string;
  headingAccent?: string;
  quickAnswerLabel?: string;
  quickAnswer?: string;
  ctaLabel?: string;
  ctaLink?: string;
  secondaryCtaLabel?: string;
  secondaryCtaLink?: string;
  microText?: string;
  image?: { url?: string; alt?: string } | null;
  showBrowserFrame?: boolean;
  browserUrl?: string;
  chipTitle?: string;
  chipSubtitle?: string;
  ratings?: Rating[];
};

/** Render `**bold**` → <strong>…</strong>; everything else as plain text. */
function renderRichInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

function Stars({ filled = 5 }: { filled?: number }) {
  const n = Math.max(0, Math.min(5, Math.round(filled)));
  return (
    <span className="hb-stars" aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < n ? '' : 'hb-dim'}>
          ★
        </span>
      ))}
    </span>
  );
}

function PlatformLogo({ platform }: { platform?: Rating['platform'] }) {
  if (platform === 'capterra') {
    return (
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width={28} height={28}>
        <path fill="#2C8FD6" d="M22 2 2 9.8l8 3.1L22 2Z" />
        <path fill="#FF6D3B" d="M22 2 10 12.9 13.2 21 22 2Z" />
      </svg>
    );
  }
  if (platform === 'g2') {
    return <span className="hb-g2">G2</span>;
  }
  if (platform === 'google') {
    return (
      <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" width={30} height={30}>
        <path
          fill="#4285F4"
          d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
        />
        <path
          fill="#34A853"
          d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
        />
        <path
          fill="#FBBC05"
          d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
        />
        <path
          fill="#EA4335"
          d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
        />
      </svg>
    );
  }
  // 'other' or unknown → neutral dot
  return <span className="hb-dot" />;
}

export function HeroBlockBRenderer({ block }: { block: HeroBBlock }) {
  const ratings = block.ratings ?? [];

  return (
    <section className="hb-hero">
      <span className="hb-blob hb-blob-a" />
      <span className="hb-blob hb-blob-b" />

      <div className="hb-wrap">
        <div className="hb-grid">
          {/* LEFT */}
          <div className="hb-copy">
            {(block.eyebrowBold || block.eyebrowText) && (
              <span className="hb-eyebrow hb-anim hb-d1">
                {block.eyebrowBadge && (
                  <span className="hb-g-badge">{block.eyebrowBadge}</span>
                )}
                {block.eyebrowBold && <b>{block.eyebrowBold}</b>}
                {block.eyebrowText && <> · {block.eyebrowText}</>}
              </span>
            )}

            <h1 className="hb-anim hb-d2">
              {block.heading}
              {block.headingAccent && (
                <>
                  {' '}
                  <span className="hb-accent">{block.headingAccent}</span>
                </>
              )}
            </h1>

            {(block.quickAnswerLabel || block.quickAnswer) && (
              <div className="hb-qa hb-anim hb-d3">
                {block.quickAnswerLabel && (
                  <span className="hb-qa-label">
                    <span className="hb-qa-spark">⚡</span>
                    {block.quickAnswerLabel}
                  </span>
                )}
                {block.quickAnswer && <p>{renderRichInline(block.quickAnswer)}</p>}
              </div>
            )}

            {(block.ctaLabel || block.secondaryCtaLabel) && (
              <div className="hb-cta-row hb-anim hb-d4">
                {block.ctaLabel && block.ctaLink && (
                  <Link href={block.ctaLink} className="hb-btn hb-btn-primary">
                    {block.ctaLabel}
                  </Link>
                )}
                {block.secondaryCtaLabel && block.secondaryCtaLink && (
                  <Link href={block.secondaryCtaLink} className="hb-btn hb-btn-ghost">
                    <span className="hb-play">▶</span>
                    {block.secondaryCtaLabel}
                  </Link>
                )}
              </div>
            )}

            {block.microText && (
              <p className="hb-micro hb-anim hb-d4">
                <span className="hb-tick">✓</span> {block.microText}
              </p>
            )}
          </div>

          {/* RIGHT */}
          <div className="hb-visual hb-anim hb-d5">
            <div className={`hb-frame${block.showBrowserFrame === false ? ' hb-frame--bare' : ''}`}>
              {/* Browser-chrome bar — optional (defaults to shown for existing content) */}
              {block.showBrowserFrame !== false && (
                <div className="hb-bar">
                  <i /><i /><i />
                  <span className="hb-url">{block.browserUrl || 'app.officeportal.io'}</span>
                </div>
              )}
              {block.image?.url ? (
                <div className="hb-img-wrap">
                  <PayloadImage
                    media={block.image}
                    alt={block.image.alt || block.heading || 'Hero screenshot'}
                    targetWidth={1600}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="hb-img"
                    priority
                  />
                </div>
              ) : (
                <div className="hb-body">
                  <div className="hb-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect
                        x="2.5"
                        y="4"
                        width="19"
                        height="16"
                        rx="3"
                        stroke="#13414A"
                        strokeWidth="1.8"
                      />
                      <circle cx="8" cy="9.5" r="2" fill="#EC5E2A" />
                      <path
                        d="M3.5 17.5L9 12l3.5 3 4-4.5L20.5 16"
                        stroke="#13414A"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <div className="hb-ph-title">Product Screenshot</div>
                    <div className="hb-ph-sub">
                      Replace with a real OfficePortal dashboard view
                    </div>
                  </div>
                  <span className="hb-ph-tag">IMAGE PLACEHOLDER · 1280 × 960</span>
                </div>
              )}
            </div>

            {(block.chipTitle || block.chipSubtitle) && (
              <div className="hb-chip">
                <span className="hb-chip-ic">✓</span>
                <span className="hb-chip-tx">
                  {block.chipTitle && <b>{block.chipTitle}</b>}
                  {block.chipSubtitle && <span>{block.chipSubtitle}</span>}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {ratings.length > 0 && (
        <div className="hb-rating-strip hb-anim hb-d6">
          <div className="hb-rating-inner">
            {ratings.map((r, i) => (
              <div className="hb-rate-item" key={i}>
                <span className="hb-rate-logo">
                  <PlatformLogo platform={r.platform} />
                </span>
                <div className="hb-rate-meta">
                  <Stars filled={r.stars ?? 5} />
                  <div className="hb-rate-label">
                    <b>{r.score}</b> on {r.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <style jsx global>{`
        .hb-hero {
          --teal-glow: #1d656d;
          --teal-mid: #154e57;
          --teal-base: #0e444c;
          --teal-deep: #0b3138;
          --hb-orange: #ec5e2a;
          --hb-orange-deep: #d44e1e;
          --hb-gold: #f4a02c;
          --hb-text-soft: #a8c2c5;
          --hb-text-mute: #7b989c;
          --hb-line: rgba(255, 255, 255, 0.13);
          --hb-panel: rgba(255, 255, 255, 0.055);
          position: relative;
          overflow: hidden;
          padding: 130px 0 54px;
          color: #fff;
          font-family: 'DM Sans', system-ui, -apple-system, Segoe UI, sans-serif;
          background:
            radial-gradient(78% 66% at 50% 20%, var(--teal-glow) 0%, var(--teal-mid) 40%, var(--teal-base) 72%),
            var(--teal-base);
        }
        .hb-hero::after {
          content: '';
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(112% 92% at 50% 34%, transparent 48%, rgba(5, 26, 30, 0.62) 100%);
        }
        .hb-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
        }
        .hb-blob-a {
          width: 360px;
          height: 360px;
          background: rgba(43, 122, 131, 0.55);
          top: -130px;
          left: 30%;
        }
        .hb-blob-b {
          width: 300px;
          height: 300px;
          background: rgba(236, 94, 42, 0.16);
          bottom: -40px;
          right: -60px;
        }
        .hb-wrap {
          position: relative;
          z-index: 3;
          max-width: 1220px;
          margin: 0 auto;
          padding: 0 46px;
        }
        .hb-grid {
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: 54px;
          align-items: center;
          margin-top: 26px;
        }

        .hb-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          font-size: 13.5px;
          color: var(--hb-text-soft);
        }
        .hb-g-badge {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          flex: none;
          background: #2da94f;
          color: #fff;
          display: grid;
          place-items: center;
          font-weight: 800;
          font-size: 13px;
        }
        .hb-eyebrow b {
          color: #fff;
          font-weight: 700;
        }

        .hb-copy :global(h1),
        .hb-copy h1 {
          font-family: 'Sora', 'DM Sans', sans-serif;
          letter-spacing: -0.025em;
          line-height: 1.1;
          font-size: clamp(34px, 4.5vw, 53px);
          font-weight: 800;
          color: #fff;
          margin: 18px 0 0;
        }
        .hb-accent {
          color: var(--hb-orange);
        }

        .hb-qa {
          margin-top: 24px;
          background: var(--hb-panel);
          border: 1px solid var(--hb-line);
          border-left: 4px solid var(--hb-orange);
          border-radius: 14px;
          padding: 17px 20px;
          backdrop-filter: blur(4px);
        }
        .hb-qa-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'Sora', sans-serif;
          font-weight: 700;
          font-size: 11.5px;
          letter-spacing: 0.11em;
          text-transform: uppercase;
          color: var(--hb-orange);
        }
        .hb-qa-spark {
          width: 19px;
          height: 19px;
          border-radius: 6px;
          background: var(--hb-orange);
          color: #fff;
          display: grid;
          place-items: center;
          font-size: 11px;
        }
        .hb-qa p {
          margin-top: 10px;
          font-size: 14.5px;
          line-height: 1.64;
          color: var(--hb-text-soft);
        }
        .hb-qa p :global(strong) {
          color: #fff;
          font-weight: 600;
        }

        .hb-cta-row {
          display: flex;
          gap: 13px;
          flex-wrap: wrap;
          margin-top: 26px;
        }
        /* :global() because next/link renders <a> as a component child and
           styled-jsx can't scope-hash component classes — only native JSX. */
        .hb-cta-row :global(.hb-btn) {
          font-family: 'Sora', sans-serif;
          font-weight: 600;
          font-size: 15px;
          border: none;
          cursor: pointer;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 15px 27px;
          transition: 0.22s ease;
          text-decoration: none;
          line-height: 1;
        }
        .hb-cta-row :global(.hb-btn-primary) {
          background: linear-gradient(135deg, var(--hb-orange), var(--hb-orange-deep));
          color: #fff;
          box-shadow: 0 12px 26px rgba(236, 94, 42, 0.36);
        }
        .hb-cta-row :global(.hb-btn-primary):hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 34px rgba(236, 94, 42, 0.46);
        }
        .hb-cta-row :global(.hb-btn-ghost) {
          background: rgba(255, 255, 255, 0.06);
          color: #fff;
          border: 1.6px solid var(--hb-line);
        }
        .hb-cta-row :global(.hb-btn-ghost):hover {
          background: rgba(255, 255, 255, 0.12);
          border-color: rgba(255, 255, 255, 0.3);
        }
        .hb-play {
          width: 23px;
          height: 23px;
          border-radius: 50%;
          border: 1.6px solid rgba(255, 255, 255, 0.55);
          display: grid;
          place-items: center;
          font-size: 9px;
          padding-left: 2px;
        }
        .hb-micro {
          margin-top: 15px;
          font-size: 12.5px;
          color: var(--hb-text-mute);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .hb-tick {
          color: var(--hb-gold);
          font-weight: 800;
        }

        .hb-visual {
          position: relative;
        }
        .hb-frame {
          background: #fff;
          border-radius: 22px;
          box-shadow: 0 36px 70px rgba(5, 26, 30, 0.55);
          overflow: hidden;
        }
        /* Frame toggle off → drop the white card + shadow so the image floats clean */
        .hb-frame--bare {
          background: transparent;
          box-shadow: none;
        }
        .hb-bar {
          display: flex;
          align-items: center;
          gap: 7px;
          padding: 13px 16px;
          background: #edf0f1;
        }
        .hb-bar i {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          display: block;
          background: #c2c9cb;
        }
        .hb-url {
          margin: 0 auto;
          background: #fff;
          border-radius: 7px;
          padding: 5px 22px;
          font-size: 11.5px;
          font-weight: 600;
          color: #8a9598;
        }
        .hb-img-wrap :global(img),
        .hb-img-wrap :global(.hb-img) {
          width: 100%;
          height: auto;
          display: block;
        }
        .hb-body {
          aspect-ratio: 4 / 3;
          background:
            repeating-linear-gradient(135deg, rgba(21, 78, 87, 0.045) 0 14px, transparent 14px 28px),
            linear-gradient(160deg, #e8f2f2 0%, #fcede7 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
        }
        .hb-icon {
          width: 76px;
          height: 76px;
          border-radius: 20px;
          background: #fff;
          box-shadow: 0 12px 24px rgba(11, 49, 56, 0.14);
          display: grid;
          place-items: center;
        }
        .hb-icon svg {
          width: 35px;
          height: 35px;
        }
        .hb-ph-title {
          font-family: 'Sora', sans-serif;
          font-weight: 700;
          font-size: 15px;
          color: #13414a;
          text-align: center;
        }
        .hb-ph-sub {
          font-size: 12.5px;
          color: #7e9498;
          text-align: center;
        }
        .hb-ph-tag {
          font-family: 'Sora', sans-serif;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.05em;
          color: var(--hb-orange-deep);
          background: #fff;
          border: 1px solid #f6d9cd;
          padding: 6px 12px;
          border-radius: 99px;
        }
        .hb-chip {
          position: absolute;
          left: -28px;
          bottom: 46px;
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 20px 38px rgba(5, 26, 30, 0.4);
          padding: 12px 15px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .hb-chip-ic {
          width: 38px;
          height: 38px;
          border-radius: 11px;
          background: #e4f5ef;
          color: #1fa97a;
          display: grid;
          place-items: center;
          font-size: 18px;
        }
        .hb-chip-tx b {
          font-family: 'Sora', sans-serif;
          font-size: 13px;
          color: #13414a;
          display: block;
        }
        .hb-chip-tx span {
          font-size: 11px;
          color: #8a9598;
        }

        .hb-rating-strip {
          position: relative;
          z-index: 3;
          max-width: 1220px;
          margin: 0 auto;
          padding: 0 46px;
        }
        .hb-rating-inner {
          margin-top: 36px;
          padding-top: 22px;
          border-top: 1px solid var(--hb-line);
          display: flex;
          align-items: center;
          gap: 12px 36px;
          flex-wrap: wrap;
        }
        .hb-rate-item {
          display: flex;
          align-items: center;
          gap: 11px;
        }
        .hb-rate-logo {
          width: 34px;
          height: 34px;
          flex: none;
          display: grid;
          place-items: center;
        }
        .hb-g2 {
          width: 31px;
          height: 31px;
          border-radius: 8px;
          background: #ef4a23;
          color: #fff;
          display: grid;
          place-items: center;
          font-family: 'Sora', sans-serif;
          font-weight: 800;
          font-size: 14px;
        }
        .hb-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.4);
        }
        .hb-stars {
          color: var(--hb-gold);
          font-size: 13px;
          letter-spacing: 1.6px;
          line-height: 1;
        }
        .hb-stars .hb-dim {
          color: rgba(244, 160, 44, 0.3);
        }
        .hb-rate-label {
          margin-top: 3px;
          font-size: 12px;
          color: var(--hb-text-mute);
        }
        .hb-rate-label b {
          font-family: 'Sora', sans-serif;
          font-weight: 700;
          color: #fff;
          font-size: 12.5px;
        }

        @keyframes hb-rise {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hb-anim { opacity: 0; animation: hb-rise 0.7s cubic-bezier(0.22, 0.7, 0.3, 1) forwards; }
        .hb-d1 { animation-delay: 0.05s; }
        .hb-d2 { animation-delay: 0.15s; }
        .hb-d3 { animation-delay: 0.25s; }
        .hb-d4 { animation-delay: 0.35s; }
        .hb-d5 { animation-delay: 0.46s; }
        .hb-d6 { animation-delay: 0.6s; }

        @media (max-width: 900px) {
          .hb-hero { padding-top: 100px; }
          .hb-grid { grid-template-columns: 1fr; gap: 42px; }
          .hb-wrap, .hb-rating-strip { padding: 0 22px; }
          .hb-rate-item { flex: 1 1 auto; }
          .hb-chip { left: auto; right: 14px; bottom: 14px; }
        }
        @media (max-width: 560px) {
          .hb-hero { padding-top: 90px; }
          .hb-cta-row :global(.hb-btn) { flex: 1 1 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
}
